import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  ArrowRight, Heart, Lightbulb, Users2, Compass, Target, BookOpen, Award, GraduationCap, Globe, Sparkles,
} from "lucide-react";

const heroImg = "https://customer-assets.emergentagent.com/job_speech-therapy-aec/artifacts/cr1xlef7_IMG_2416.webp";
const founderImg = "https://customer-assets.emergentagent.com/job_speech-therapy-aec/artifacts/j2cxo5pm_IMG_1133.jpeg";

const Para = ({ children }) => (
  <p className="text-[#52606D] leading-relaxed">{children}</p>
);

const Chip = ({ children }) => (
  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-[#F4F5F0] text-[#2C4C3B] border border-[#E2E8F0]" data-testid="about-chip">
    {children}
  </span>
);

export default function About() {
  const certifications = [
    "Advanced AI Prompt Engineering",
    "PROMPT",
    "NLA",
    "Inside Out Sensory (OSIT)",
    "PECS",
    "ACAP by Avaz",
    "AAC by SpeechGears",
    "BG and Reflex Integration",
    "Child Psychology",
    "Learning Disability",
    "Neurodiversity-Affirming Goal Writing",
  ];

  const trainings = [
    "Red Flags of Goal Writing",
    "Visual Supports",
    "Addressing Speech Difficulties",
    "Sensory Modulation Disorder",
    "Navigating Autism: Assessment and Goal Setting",
    "Behavioural Challenges in Autism Therapy",
    "Stuttering: Assessment and Intervention",
    "Behavioural Insights",
  ];

  return (
    <div data-testid="about-page">
      {/* ABOUT COMMPASS */}
      <section className="organic-bg">
        <div className="max-w-6xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">About CommPass</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#2C4C3B] mt-3 leading-[1.05]">
              Programme AAC devices<br />
              <span className="text-[#C66A51]">one activity, one stage</span> at a time.
            </h1>
            <p className="mt-5 text-lg text-[#2C4C3B] font-medium">
              Built in the clinic. Designed for every communicator.
            </p>
          </div>
          <div className="lg:col-span-5">
            <img src={heroImg} alt="Individual-led play with colourful blocks" className="rounded-3xl shadow-xl object-cover w-full h-[440px]" />
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-20 space-y-6" data-testid="about-commpass-body">
        <Para>
          CommPass is the <strong className="text-[#C66A51]">only global</strong> clinician-built AAC programming and goal-setting platform designed
          specifically for speech-language pathologists.
        </Para>
        <Para>
          Built by an SLP, for clinicians who work in the real world, CommPass addresses a frustration every
          AAC clinician knows: hours spent assembling activity-specific vocabulary lists and goals from
          scratch, every single week, for every single individual.
        </Para>
        <Para>
          CommPass changes that. It generates personalised AAC programming word lists and SMART goal banks
          in minutes, shaped by four clinical filters: <strong className="text-[#2C4C3B]">activity context, differences profile, AAC communicator stage,
          and profile-specific severity or stage.</strong> The result is targeted, neurodiversity-affirming,
          and ready to use.
        </Para>
        <Para>
          The platform is grounded in an evidence-based framework pairing <strong className="text-[#2C4C3B]">Trina Becker's AAC communicator stages</strong>,
          the <strong className="text-[#2C4C3B]">80/20 core-fringe ratio</strong>, and <strong className="text-[#2C4C3B]">Janice Light's four competency domains</strong>
          with on-demand AI generation that never loses sight of the individual in front of you.
        </Para>
        <Para>
          CommPass does not replace clinical judgment. It sharpens it. Faster, more targeted, and always
          individual-centred.
        </Para>
        <Para>
          The first product of <span className="text-[#C66A51] font-medium">Sensiiq</span>, a clinician-built
          studio dedicated to giving time back to the people who need it most: the individual.
        </Para>
      </section>

      {/* Two modules */}
      <section className="bg-[#F4F5F0] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">Two modules</span>
          <h2 className="font-display text-3xl sm:text-4xl text-[#2C4C3B] mt-2">Programming. Goals. One clinical backbone.</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <div className="p-8 rounded-2xl bg-white border border-[#E2E8F0] card-lift">
              <div className="w-11 h-11 rounded-xl bg-[#E6F0E9] text-[#2C4C3B] flex items-center justify-center mb-5">
                <BookOpen className="w-5 h-5" />
              </div>
              <h3 className="font-display text-xl text-[#2C4C3B]">Programming</h3>
              <p className="text-sm text-[#52606D] mt-2 leading-relaxed">
                Choose activity, diagnosis, AAC communicator stage, severity and device — get a clinical
                programming table with at least 50 modelling examples plus core/fringe banks.
              </p>
              <Link to="/search" className="inline-flex items-center gap-2 mt-4 text-sm text-[#2C4C3B] font-medium hover:underline">
                Open Programming <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="p-8 rounded-2xl bg-white border border-[#E2E8F0] card-lift">
              <div className="w-11 h-11 rounded-xl bg-[#FCEAE5] text-[#9C422A] flex items-center justify-center mb-5">
                <Target className="w-5 h-5" />
              </div>
              <h3 className="font-display text-xl text-[#2C4C3B]">Goals</h3>
              <p className="text-sm text-[#52606D] mt-2 leading-relaxed">
                Generate SMART goal banks across the four AAC competency domains (Janice Light, 1989):
                Linguistic, Social, Strategic and Operational — with prompt levels and timeframes.
              </p>
              <Link to="/goals" className="inline-flex items-center gap-2 mt-4 text-sm text-[#2C4C3B] font-medium hover:underline">
                Open Goals <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT THE FOUNDER */}
      <section className="max-w-6xl mx-auto px-6 py-24" data-testid="about-founder">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="mb-8 overflow-hidden rounded-3xl border border-[#E2E8F0] shadow-lg" data-testid="founder-image-wrap">
              <img
                src={founderImg}
                alt="Sadiya Yasin — Speech-Language Pathologist and Founder of Sensiiq"
                className="w-full h-auto object-cover aspect-[4/5]"
                data-testid="founder-image"
              />
            </div>
            <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">About the founder</span>
            <h2 className="font-display text-4xl sm:text-5xl text-[#2C4C3B] mt-3 leading-[1.1]">Sadiya Yasin</h2>
            <p className="mt-3 text-sm tracking-[0.2em] uppercase text-[#52606D]">Speech-Language Pathologist · CEO &amp; Founder, Sensiiq</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Clinician", "Innovator", "Advocate"].map((r) => (
                <span key={r} className="px-3 py-1.5 rounded-full text-xs font-semibold tracking-[0.1em] uppercase bg-[#2C4C3B] text-white">{r}</span>
              ))}
            </div>
            <p className="mt-8 font-display text-xl text-[#2C4C3B] leading-snug">
              Building tools and frameworks that put the individual first and the clinician back in control.
            </p>
          </div>

          <div className="lg:col-span-7 space-y-5">
            <Para>
              Sadiya Yasin is a <strong className="text-[#2C4C3B]">first-class graduate speech-language pathologist</strong> with experience across
              hospitals, schools, outpatient clinics, and private practices in India and Singapore. She
              works with children and adults across speech delay, autism, gestalt language processing,
              acquired aphasia, and complex communication needs.
            </Para>
            <Para>
              What sets Sadiya apart is her commitment to innovative, integrative practice. At Sensiiq, she
              combines evidence-based approaches including <strong className="text-[#2C4C3B]">PROMPT, Natural Language Acquisition, Reflex
              Integration, Oral Sensory Integration, gestalt language processing</strong>, and <strong className="text-[#2C4C3B]">advanced AI prompt
              engineering</strong> into a seamless, whole-person framework. This is not a one-method practice. It
              is a living, evolving clinical model built around the individual, not the diagnosis.
            </Para>
            <Para>
              Her practice is built on <strong className="text-[#2C4C3B]">meeting families where they are. Listening. Advocating. Delivering
              care that goes beyond a referral.</strong> Sadiya supports clinicians and families in understanding
              how to help their loved ones communicate in ways that are clear, affirming, and genuinely
              accessible.
            </Para>
            <Para>
              Sessions at Sensiiq centre on <strong className="text-[#2C4C3B]">connection, learning, and celebrating every small step</strong> forward
              using approaches that hold space for both growth and joy. She leads a small, dedicated team
              of therapists who share the same commitment to compassionate, meaningful care.
            </Para>
            <Para>
              Outside of work, you will find her reading, travelling, riding her Harley, or creating content
              that matters. She believes deeply in the power of mindset and positive living, and enjoys
              guiding others toward confidence, balance, and emotional growth.
            </Para>
          </div>
        </div>

        {/* Certifications */}
        <div className="mt-16 grid lg:grid-cols-2 gap-8">
          <div className="p-8 rounded-2xl bg-white border border-[#E2E8F0]" data-testid="about-certifications">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-[#E6F0E9] text-[#2C4C3B] flex items-center justify-center">
                <Award className="w-5 h-5" />
              </div>
              <h3 className="font-display text-xl text-[#2C4C3B]">Certified In</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {certifications.map((c) => <Chip key={c}>{c}</Chip>)}
            </div>
          </div>
          <div className="p-8 rounded-2xl bg-white border border-[#E2E8F0]" data-testid="about-trainings">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-[#FCEAE5] text-[#9C422A] flex items-center justify-center">
                <GraduationCap className="w-5 h-5" />
              </div>
              <h3 className="font-display text-xl text-[#2C4C3B]">Trained In</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {trainings.map((t) => <Chip key={t}>{t}</Chip>)}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 p-8 rounded-2xl bg-[#2C4C3B] text-white flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase opacity-70">Try CommPass</div>
            <div className="font-display text-xl mt-1">Five steps from activity to programming table.</div>
          </div>
          <Link to="/search">
            <Button className="rounded-full bg-[#C66A51] hover:bg-[#A85639] text-white px-7 py-6" data-testid="about-cta">
              Start using CommPass <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
